import {
  type ReactNode,
  Suspense,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { fetchAuthStatus, logout, onUnauthorized } from '../../shared/api';
import { LogOut } from '../../shared/icons';
import LoginView from '../../shared/LoginView';
import Launcher from './Launcher';
import { type AppKey, apps, HOME, selectKey } from './registry';

/** 非激活的应用保持挂载但隐藏（keep-alive），切换回来时状态不丢 */
function Pane({ active, children }: { active: boolean; children: ReactNode }) {
  return <div className={active ? 'h-full' : 'hidden'}>{children}</div>;
}

/** 登录门状态：检查中 / 需登录 / 已放行 */
type AuthPhase = 'checking' | 'locked' | 'open';

/**
 * home 应用即整个系统的外壳：登录门 + 左侧 dock + 右侧应用面板。
 * 与 server 端的 SPA fallback 配合，任意路径刷新都会回到这里，
 * 再由 selectKey 恢复到对应应用（/notebook/* → notebook，以此类推）。
 * 启动时查 /api/auth/status 决定是否先过登录门；任一业务请求
 * 返回 401（会话失效）时经 onUnauthorized 切回登录门。
 */
const App = () => {
  const [phase, setPhase] = useState<AuthPhase>('checking');
  // 密码启用时才在 dock 底部展示登出按钮。
  const [authEnabled, setAuthEnabled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchAuthStatus().then((status) => {
      if (cancelled) return;
      if (!status?.enabled) {
        setAuthEnabled(false);
        setPhase('open');
      } else {
        setAuthEnabled(true);
        setPhase(status.authenticated ? 'open' : 'locked');
      }
    });
    const off = onUnauthorized(() => setPhase('locked'));
    return () => {
      cancelled = true;
      off();
    };
  }, []);

  const handleLogout = useCallback(async () => {
    await logout();
    setPhase('locked');
  }, []);

  const [key, setKey] = useState<AppKey>(() =>
    selectKey(window.location.pathname),
  );
  // 首次激活某应用时才挂载，之后保持挂载
  const [mounted, setMounted] = useState<ReadonlySet<AppKey>>(() => new Set());

  useEffect(() => {
    setMounted((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  }, [key]);

  // 标题跟随当前应用（子应用挂载时也会各自设置，这里兜底覆盖切换场景）
  useEffect(() => {
    document.title = key === 'home' ? 'NotaOne' : `${key} · NotaOne`;
  }, [key]);

  const navigate = useCallback((path: string) => {
    if (window.location.pathname + window.location.search !== path) {
      window.history.pushState(null, '', path);
    }
    setKey(selectKey(window.location.pathname));
    // 手动补发 popstate：path 里的查询参数（如 korean 深链
    // /korean/?tab=words&leech=1）靠它同步到已挂载的子应用
    window.dispatchEvent(new PopStateEvent('popstate'));
  }, []);

  // 浏览器前进 / 后退
  useEffect(() => {
    const onPopState = () => setKey(selectKey(window.location.pathname));
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  // 拦截站内 <a> 链接做客户端导航，避免整页刷新破坏 dock 布局
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (
        e.defaultPrevented ||
        e.button !== 0 ||
        e.metaKey ||
        e.ctrlKey ||
        e.shiftKey ||
        e.altKey
      ) {
        return;
      }
      const anchor = e.target instanceof Element ? e.target.closest('a') : null;
      if (!anchor) return;
      const href = anchor.getAttribute('href');
      if (!href?.startsWith('/')) return;
      const url = new URL(anchor.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      e.preventDefault();
      navigate(url.pathname + url.search);
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [navigate]);

  if (phase === 'checking') {
    return (
      <div className="flex h-full items-center justify-center text-sm text-neutral-400">
        加载中…
      </div>
    );
  }

  if (phase === 'locked') {
    return <LoginView onSuccess={() => setPhase('open')} />;
  }

  return (
    <div className="flex h-full bg-neutral-50 text-neutral-800">
      {/* 左侧 dock */}
      <nav className="flex w-16 shrink-0 flex-col items-center border-r border-neutral-200 bg-white py-4">
        <div className="flex size-9 select-none items-center justify-center rounded-xl bg-neutral-900 text-sm font-bold text-white">
          n
        </div>
        <div className="my-3 h-px w-8 bg-neutral-200" />
        <ul className="flex flex-col items-center gap-1.5">
          {[HOME, ...apps.filter((app) => !app.hidden)].map((app) => {
            const Icon = app.icon;
            const active = app.key === key;
            return (
              <li key={app.key}>
                <button
                  type="button"
                  title={app.title}
                  aria-label={app.title}
                  aria-current={active ? 'page' : undefined}
                  onClick={() => navigate(app.path)}
                  className={`flex size-11 items-center justify-center rounded-xl transition-colors ${
                    active
                      ? 'bg-neutral-900 text-white'
                      : 'text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900'
                  }`}
                >
                  <Icon className="size-5" />
                </button>
              </li>
            );
          })}
        </ul>

        {/* 登出：仅在密码启用时展示，钉在 dock 底部 */}
        {authEnabled && (
          <div className="mt-auto pt-3">
            <button
              type="button"
              title="登出"
              aria-label="登出"
              onClick={handleLogout}
              className="flex size-11 items-center justify-center rounded-xl text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-700"
            >
              <LogOut className="size-5" />
            </button>
          </div>
        )}
      </nav>

      {/* 右侧应用面板 */}
      <main className="relative min-w-0 flex-1">
        <Pane active={key === 'home'}>
          <Launcher />
        </Pane>
        {apps.map((app) =>
          mounted.has(app.key) && app.Component ? (
            <Pane key={app.key} active={key === app.key}>
              <Suspense
                fallback={
                  <div className="flex h-full items-center justify-center text-sm text-neutral-400">
                    加载中…
                  </div>
                }
              >
                <app.Component />
              </Suspense>
            </Pane>
          ) : null,
        )}
      </main>
    </div>
  );
};

export default App;
